// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx

/**
 * A past review the answer cited, drawn like an Output review card: the
 * review's title, then the finding as the secondary line.
 */

import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { AskV2RailReviewCard } from "./AskV2RailReviewCard";
import { type RailFindingCitation } from "./groupRailCitations";

import { artworkPreviewSource } from "../../components/ask-puntt-review/artworkPreviewSource";
import { resolveReviewListCardThumbnail } from "../../components/ask-puntt-review/resolveReviewListCardThumbnail";
import { isDemoEnterprise } from "../../utils/auth";
import { buildAskV2ChatReviewPath } from "../paths";
import { useAskPunttReview } from "../review/useAskPunttReview";

const EVIDENCE_TAG_CLASS = "puntt-link";

export function AskV2RailCitedFinding({
  finding,
}: {
  finding: RailFindingCitation;
}) {
  const { t } = useTranslation("ui");
  const reviewId = finding.review?.reviewId;
  const { data } = useAskPunttReview(reviewId);
  const live = data?.review;
  const title = live?.title?.trim() || "";

  if (!finding.review) {
    return (
      <li className="flex items-start gap-1.5">
        <span
          data-testid={`ask-v2-rail-evidence-tag-review-${finding.id}`}
          className={`shrink-0 rounded-full bg-puntt-neutral-gray-3 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${EVIDENCE_TAG_CLASS}`}
        >
          {t("askV2.rail.evidenceTagReview")}
        </span>
        <Text
          as="span"
          data-testid={`ask-v2-rail-cited-finding-${finding.id}`}
          className="text-[13px] leading-snug"
        >
          {finding.text}
        </Text>
      </li>
    );
  }

  const link = buildAskV2ChatReviewPath(
    finding.review.chatId,
    finding.review.reviewId,
    finding.review.commentId,
  );
  const name = title || t("askV2.rail.citedFindingUntitled");
  const thumbnailUrl = resolveReviewListCardThumbnail({
    review: live,
    isDemoEnterprise: isDemoEnterprise(),
  });
  const artworkPreviewUrl = thumbnailUrl
    ? undefined
    : artworkPreviewSource(live?.files);

  return (
    <li>
      <AskV2RailReviewCard
        testIdPrefix="ask-v2-rail-cited-finding"
        data-testid={`ask-v2-rail-cited-finding-${finding.id}`}
        to={link.to}
        params={{
          chatId: finding.review.chatId,
          reviewId: finding.review.reviewId,
          commentId: finding.review.commentId,
        }}
        ariaLabel={t("askV2.rail.citedFindingAria", {
          text: title ? `${name}: ${finding.text}` : finding.text,
        })}
        item={{
          id: finding.id,
          name,
          detail: finding.text,
          versionNumber: live?.versionNumber,
          thumbnailUrl,
          artworkPreviewUrl,
        }}
      />
    </li>
  );
}
